import { Listbox } from '@headlessui/react';
import { useRef } from 'react';
import { Portal } from 'react-portal';

import { CheckIcon } from '~components/icons/CheckIcon';
import { usePopper } from '~hooks/usePopper';
import { CropFormatList } from '~modules/media-library/model/CropFormat';
import { SelectButton } from '~modules/media-library/view/SelectButton';

type SelectCropFormatProps = {
  cropFormats: CropFormatList;
  selected: number;
  onSelect: (cropFormatIdx: number) => void;
  disabled?: boolean;
};

export function SelectCropFormat({
  cropFormats,
  selected: selectedCropFormatIndex,
  onSelect: onSelectCropFormatIndex,
  disabled = false,
}: SelectCropFormatProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  let [trigger, container] = usePopper({
    placement: 'bottom',
    strategy: 'fixed',
  });

  // Derived value
  const selectedCropFormat = cropFormats?.[selectedCropFormatIndex];

  return (
    <Listbox disabled={disabled} value={selectedCropFormatIndex} onChange={onSelectCropFormatIndex}>
      <div className="relative mt-1" ref={parentRef}>
        <Listbox.Button as={SelectButton} className="w-full justify-between" ref={trigger}>
          <span className="block truncate">{selectedCropFormat?.label || ''}</span>
        </Listbox.Button>

        <Portal>
          <Listbox.Options
            className="m-0 p-0 max-h-60 overflow-auto rounded bg-white text-base shadow-md outline-none text-sm z-50"
            ref={container}
            style={{ width: parentRef?.current?.offsetWidth }}
          >
            {cropFormats.map((cropFormat, cropFormatIdx) => (
              <Listbox.Option
                key={cropFormatIdx}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-8 pr-4 ${
                    active ? 'bg-blue text-white' : 'text-darkgray'
                  }`
                }
                value={cropFormatIdx}
              >
                {({ selected }) => (
                  <>
                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                      {cropFormat.label}
                    </span>
                    {selected ? (
                      <span className="absolute top-0 bottom-0 left-0 flex items-center pl-2 text-blue">
                        <CheckIcon className="h-4 w-4" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Portal>
      </div>
    </Listbox>
  );
}
